// Wing slice/section/carve client (FXC-9009 / blown-lift × Tailor — 3D lift).
//
// Lifts the 2D high-lift workflow onto a 3D wing: slice a STEP geometry (or a
// parametric planform) into spanwise eta sections, attach a per-section airfoil
// design + flap hinge, then carve the lofted solid for meshing/solve.
//
// Server: thread/thread-api/app/routes/wing.py
//   POST /projects/{id}/wing/slice
//   POST /projects/{id}/wing/sections
//   GET  /projects/{id}/wing/{wing_id}/sections?branch=
//   POST /projects/{id}/wing/carve
//
// Sections live as nodes under the wing; a SectionSummary is the source-neutral
// per-station record the explorer reads back.

import { apiClient } from "./client";

// --- shared planform geometry ------------------------------------------------

/** Trapezoidal planform the slice/sections endpoints lay sections onto. */
export interface WingPlanform {
  root_le_x: number;
  tip_le_x: number;
  root_chord: number;
  tip_chord: number;
  span: number;
  y_root: number;
}

/** Per-station summary returned by every wing endpoint. */
export interface SectionSummary {
  eta: number;
  path: string;
  design_hash: string;
  airfoil_ref?: string | null;
  motion_law?: string | null;
  has_design: boolean;
  solution_status?: string | null;
  linked_design_path?: string | null;
}

// --- slice -------------------------------------------------------------------

export interface SliceRequest {
  step_path?: string | null;
  geometry_node?: string | null;
  eta_stations: number[];
  name?: string;
  planform: WingPlanform;
  xc_hinge?: number;
  n_extract?: number;
  yh?: number;
  branch?: string;
}

export interface SliceResponse {
  wing_id: string;
  wing_path: string;
  project_id: string;
  sections: SectionSummary[];
  sections_eta: number[];
  step_path?: string | null;
  note?: string | null;
}

/** Slice a STEP/parametric wing into spanwise eta sections. */
export async function sliceWing(
  projectId: string,
  branch: string,
  req: SliceRequest,
): Promise<SliceResponse> {
  return apiClient<SliceResponse>(`/projects/${projectId}/wing/slice`, {
    method: "POST",
    body: { ...req, branch },
  });
}

// --- sections (upsert) -------------------------------------------------------

/** One spanwise section: where it sits + the airfoil design + flap hinge. */
export interface SectionInput {
  eta: number;
  design: Record<string, unknown>;
  airfoil_ref?: string | null;
  hinge_origin?: number[] | null;
  hinge_direction?: number[] | null;
  motion_law?: string;
  design_path?: string | null;
}

export interface SectionsRequest {
  wing_id?: string | null;
  name: string;
  planform: WingPlanform;
  span?: number | null;
  source_step_path?: string | null;
  geometry_node?: string | null;
  sections: SectionInput[];
  branch?: string;
}

export interface SectionsResponse {
  wing_id: string;
  wing_path: string;
  project_id: string;
  sections: SectionSummary[];
  linked: number;
}

/** Create/update a wing's spanwise sections (each with its design + hinge). */
export async function upsertSections(
  projectId: string,
  branch: string,
  req: SectionsRequest,
): Promise<SectionsResponse> {
  return apiClient<SectionsResponse>(`/projects/${projectId}/wing/sections`, {
    method: "POST",
    body: { ...req, branch },
  });
}

/** List a wing's current sections. */
export async function listSections(
  projectId: string,
  branch: string,
  wingId: string,
): Promise<SectionSummary[]> {
  const params = new URLSearchParams({ branch });
  return apiClient<SectionSummary[]>(
    `/projects/${projectId}/wing/${wingId}/sections?${params.toString()}`,
  );
}

// --- carve -------------------------------------------------------------------

export interface CarveRequest {
  wing_id: string;
  branch?: string;
  n_loft?: number;
  quality?: string;
  /** Per-eta flap deployment angle, keyed by eta station. */
  deploy_deg?: Record<string, number>;
}

export interface CarveResponse {
  wing_id: string;
  feature_enabled: boolean;
  step_path?: string | null;
  section_lofts?: unknown[];
  hinges?: Record<string, unknown>;
  sections?: unknown[];
  job_id?: string | null;
  /** Conveys loft_status incl. "not_implemented". */
  note?: string | null;
}

/** Carve the lofted wing solid (with per-eta flap deployment) for meshing/solve. */
export async function carveWing(
  projectId: string,
  branch: string,
  req: CarveRequest,
): Promise<CarveResponse> {
  return apiClient<CarveResponse>(`/projects/${projectId}/wing/carve`, {
    method: "POST",
    body: { ...req, branch },
  });
}

// --- cut (control-surface injection into arbitrary CAD) -----------------------

export type CutMode = "simple" | "gapped" | "sealed" | "shaped" | "design";

export interface CutRequest {
  /** Source CAD (STEP) — e.g. the sliced wing or an uploaded aircraft. */
  step_path: string;
  /** Spanwise extent of the control surface (0=root, 1=tip). */
  eta1: number;
  eta2: number;
  /** Hinge chordwise position (x/c). */
  xc_hinge?: number;
  yh?: number;
  /** simple/gapped/sealed = quick box cut; shaped = cove + spiral-LE loft. */
  mode?: CutMode;
  deflections_deg?: number[];
  gap_mm?: number;
  le_radius_mm?: number;
  /** Shaped-mode loft stations. */
  n_stations?: number;
  n_extract?: number;
  name?: string;
  /** design mode: wing whose saved parameterised foil designs drive the band. */
  wing_id?: string;
  branch?: string;
}

export interface CutOutput {
  deflection_deg: number | null;
  step_path: string;
}

export interface CutResponse {
  feature_enabled: boolean;
  mode: CutMode;
  outputs: CutOutput[];
  hinge: { origin?: number[]; direction?: number[] };
  volumes: Record<string, number>;
  planform: Record<string, number>;
  sections_cache?: string | null;
  note?: string | null;
}

/** Inject a control surface into arbitrary wing CAD: slice sections, fit the
 * planform/hinge from them, cut (quick or shaped cove), pre-rotate deflections. */
export async function cutControlSurface(
  projectId: string,
  req: CutRequest,
): Promise<CutResponse> {
  return apiClient<CutResponse>(`/projects/${projectId}/wing/cut`, {
    method: "POST",
    body: req,
  });
}

// --- small helpers -----------------------------------------------------------

/** Node path for a wing by id (matches the server's `wing.<id>` convention). */
export function wingPath(wingId: string): string {
  return `wing.${wingId}`;
}

/** Stable key for a section's eta station (3 dp, dot→p so it's path-safe). */
export function sectionEtaKey(eta: number): string {
  return `eta_${eta.toFixed(3).replace(".", "p")}`;
}
